 'use client';
import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { PageHeader } from '@/src/components/layout/site';
import AppDescription from '../year/AppDescription';
import { getCelebrations } from '../functions/holidays';
import { generateDateRangeSet } from '../functions/dateRange';
import { computeQuarterlyStats } from '../functions/quarterlyStats';
import { YearlyHeader } from '../year/components/YearlyHeader';
import { PeriodSummarySection } from '../year/components/PeriodSummarySection';
import { Instructions } from '../year/components/Instructions';
import { MonthGridSection } from '../year/components/MonthGridSection';
import { StatsSection } from '../year/components/StatsSection';

type Period = { start: string; end: string };

export default function YearlyPageContent({ year: yearText }: { year: string }) {
    const currentYear = new Date().getFullYear();
    const year = yearText ? parseInt(yearText, 10) : currentYear;

    const [periods, setPeriods] = useState<Period[]>([]);
    const [workWeek, setWorkWeek] = useState<'five' | 'six'>('five');
    const [showPeriodSummary, setShowPeriodSummary] = useState(false);

    const celebrations = getCelebrations(year);

    // Include holidays for previous, current and next year so periods
    // that cross year boundaries (e.g., include 12-31 or 01-01) will match.
    const holidayDates = new Map<string, string>();
    [year - 1, year, year + 1].forEach((y) => {
        const c = getCelebrations(y);
        c.forEach((name, date) => holidayDates.set(`${y}-${date}`, name));
    });

    const periodDays = new Set<string>();
    periods.forEach(({ start, end }) =>
        generateDateRangeSet(start, end).forEach(d => periodDays.add(d))
    );

    useEffect(() => {
        setShowPeriodSummary(periods.length > 0);
    }, [periods]);

    const { quarters5d, quarters6d } = computeQuarterlyStats(year, celebrations, periods);

    const handlePeriodChange: Dispatch<SetStateAction<Period[]>> = value => {
        setPeriods(value);
        const newPeriodsArray = typeof value === 'function' ? value(periods) : value;
        if (!showPeriodSummary && newPeriodsArray.length > 0) setShowPeriodSummary(true);
    };

    return (
        <div className="p-4 max-w-screen-xl mx-auto" style={{ userSelect: 'none' }}>
            <PageHeader title={`Darbo laiko kalendorius ${year}`} />
            <YearlyHeader
                        year={year}
                        workWeek={workWeek}
                        setWorkWeek={setWorkWeek}
                        periods={periods}
                        holidayDates={holidayDates}
                        basePath="/darbo-laiko-kalendorius/"
                    />
            {showPeriodSummary && <PeriodSummarySection periods={periods} holidayDates={holidayDates} workWeek={workWeek} />}
            <Instructions />
            <MonthGridSection
                year={year}
                periods={periods}
                setPeriods={handlePeriodChange}
                holidayDates={holidayDates}
                periodDays={periodDays}
                workWeek={workWeek}
            />
            <StatsSection quarters5d={quarters5d} quarters6d={quarters6d} workWeek={workWeek} />
            <div className="mt-8 sm:mt-10">
                <AppDescription />
            </div>
        </div>
    );
}
